// ===========================================
// Agences — Page liste des agences (refonte UX)
// ===========================================
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
    Table, Card, Input, Button, Grid, Modal, Form, Popconfirm, Space, Tooltip, Tag, Empty,
} from 'antd';
import {
    SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ShopOutlined,
    PhoneOutlined, EnvironmentOutlined, UserOutlined, IdcardOutlined,
} from '@ant-design/icons';
import { agencesService } from '../../common/services';

import MiniKpiCard from '../clients/components/MiniKpiCard';
import Can from '../../components/can';

import './agences.page.scss';

const getInitials = (nom) =>
    (nom || '')
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w[0])
        .join('')
        .substring(0, 2)
        .toUpperCase();

const Agences = () => {
    const [agences, setAgences] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [modalVisible, setModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [form] = Form.useForm();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;

    const fetchAgences = async () => {
        setLoading(true);
        try {
            const res = await agencesService.getAll();
            const data = Array.isArray(res) ? res : res?.data || [];
            setAgences(data);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAgences(); }, []);

    const filteredAgences = useMemo(() => {
        if (!search) return agences;
        const q = search.toLowerCase();
        return agences.filter((a) =>
            `${a.nom || ''} ${a.code || ''} ${a.responsable || ''} ${a.telephone || ''} ${a.adresse || ''}`
                .toLowerCase()
                .includes(q)
        );
    }, [agences, search]);

    const stats = useMemo(() => {
        const total = agences.length;
        const avecResp = agences.filter((a) => !!a.responsable).length;
        const avecTel = agences.filter((a) => !!a.telephone).length;
        return { total, avecResp, avecTel };
    }, [agences]);

    const handleSave = async (values) => {
        try {
            setModalLoading(true);
            if (editingItem) {
                await agencesService.update(editingItem._id, values);
                toast.success('Agence mise à jour');
            } else {
                await agencesService.create(values);
                toast.success('Agence créée');
            }
            setModalVisible(false);
            setEditingItem(null);
            form.resetFields();
            fetchAgences();
        } catch (error) {
            toast.error(error.message || 'Erreur');
        } finally {
            setModalLoading(false);
        }
    };

    const handleEdit = (record) => {
        setEditingItem(record);
        form.setFieldsValue(record);
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await agencesService.delete(id);
            toast.success('Agence supprimée');
            fetchAgences();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const openCreateModal = () => {
        setEditingItem(null);
        form.resetFields();
        setModalVisible(true);
    };

    const columns = [
        {
            title: 'Agence',
            dataIndex: 'nom',
            key: 'nom',
            sorter: (a, b) => (a.nom || '').localeCompare(b.nom || ''),
            render: (t) => (
                <div className="agence-cell">
                    <div className="agence-logo">{getInitials(t) || <ShopOutlined />}</div>
                    <span className="agence-name">{t || '—'}</span>
                </div>
            ),
        },
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            width: 140,
            render: (t) => t
                ? <Tag color="geekblue" className="agence-code-tag">{t}</Tag>
                : <span className="agence-muted">—</span>,
        },
        {
            title: 'Responsable',
            dataIndex: 'responsable',
            key: 'responsable',
            width: 220,
            render: (t) => t ? (
                <span className="agence-responsable">
                    <UserOutlined /> {t}
                </span>
            ) : <span className="agence-muted">—</span>,
        },
        {
            title: 'Téléphone',
            dataIndex: 'telephone',
            key: 'telephone',
            width: 170,
            render: (t) => t ? (
                <a href={`tel:${t}`} className="agence-link">
                    <PhoneOutlined /> {t}
                </a>
            ) : <span className="agence-muted">—</span>,
        },
        {
            title: 'Adresse',
            dataIndex: 'adresse',
            key: 'adresse',
            ellipsis: true,
            render: (t) => t ? (
                <span className="agence-addr">
                    <EnvironmentOutlined /> {t}
                </span>
            ) : <span className="agence-muted">—</span>,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            align: 'center',
            fixed: isMobile ? false : 'right',
            render: (_, record) => (
                <Space size={6}>
                    <Can perm="agences:update">
                        <Tooltip title="Modifier">
                            <Button
                                size="small"
                                icon={<EditOutlined />}
                                className="agence-action-btn agence-action-edit"
                                aria-label="Modifier cette agence"
                                onClick={() => handleEdit(record)}
                            />
                        </Tooltip>
                    </Can>
                    <Can perm="agences:delete">
                        <Popconfirm
                            title="Supprimer cette agence ?"
                            description="Cette action est irréversible."
                            okText="Oui"
                            cancelText="Non"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => handleDelete(record._id)}
                        >
                            <Tooltip title="Supprimer">
                                <Button
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    className="agence-action-btn agence-action-delete"
                                    aria-label="Supprimer cette agence"
                                />
                            </Tooltip>
                        </Popconfirm>
                    </Can>
                </Space>
            ),
        },
    ];

    return (
        <div className="agences-page">
            {/* Header */}
            <header className="agences-header">
                <div className="agences-header-text">
                    <h1 className="agences-title">
                        <ShopOutlined /> Gestion des agences
                    </h1>
                    <p className="agences-subtitle">Réseau d'agences et points de vente</p>
                </div>
                <div className="agences-header-actions">
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={openCreateModal}
                        className="agences-btn-add"
                    >
                        {!isMobile && 'Nouvelle agence'}
                    </Button>
                </div>
            </header>

            {/* KPIs */}
            <section className="agences-kpis" aria-label="Indicateurs agences">
                <MiniKpiCard
                    icon={<ShopOutlined />}
                    label="Total agences"
                    value={stats.total}
                    tone="blue"
                />
                <MiniKpiCard
                    icon={<UserOutlined />}
                    label="Avec responsable"
                    value={stats.avecResp}
                    tone="indigo"
                />
                <MiniKpiCard
                    icon={<PhoneOutlined />}
                    label="Avec téléphone"
                    value={stats.avecTel}
                    tone="green"
                />
            </section>

            {/* Carte principale */}
            <Card className="agences-main-card">
                <div className="agences-toolbar">
                    <Input
                        placeholder="Rechercher une agence (nom, code, responsable, téléphone)…"
                        prefix={<SearchOutlined />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        allowClear
                        className="agences-search"
                    />
                </div>

                <Table
                    dataSource={filteredAgences}
                    columns={columns}
                    rowKey="_id"
                    loading={{ spinning: loading, size: 'large', tip: 'Chargement…' }}
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: loading ? <div style={{ minHeight: 220 }} /> : <Empty description="Aucune agence" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                    }}
                    pagination={{
                        pageSize: 10,
                        showTotal: (t) => `${t} agence${t > 1 ? 's' : ''}`,
                        showSizeChanger: true,
                    }}
                />
            </Card>

            {/* Modal créer / modifier */}
            <Modal
                title={
                    <span className="agences-modal-title">
                        <ShopOutlined /> {editingItem ? "Modifier l'agence" : 'Nouvelle agence'}
                    </span>
                }
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                destroyOnClose
                width={520}
                className="agences-modal"
            >
                <Form form={form} layout="vertical" onFinish={handleSave} className="agences-form">
                    <Form.Item
                        name="nom"
                        label="Nom de l'agence"
                        rules={[{ required: true, message: "Le nom de l'agence est obligatoire" }]}
                        normalize={(v) => v?.toUpperCase()}
                    >
                        <Input
                            autoFocus
                            prefix={<ShopOutlined />}
                            placeholder="Ex : RIVIERA GOLF"
                        />
                    </Form.Item>
                    <Form.Item
                        name="code"
                        label="Code"
                        normalize={(v) => v?.toUpperCase()}
                    >
                        <Input prefix={<IdcardOutlined />} placeholder="Ex : RIVIERA" />
                    </Form.Item>
                    <Form.Item
                        name="responsable"
                        label="Responsable"
                        normalize={(v) => v?.toUpperCase()}
                    >
                        <Input prefix={<UserOutlined />} placeholder="Nom du responsable" />
                    </Form.Item>
                    <Form.Item
                        name="telephone"
                        label="Téléphone"
                        rules={[{ pattern: /^[0-9]{10}$/, message: 'Le numéro doit contenir 10 chiffres' }]}
                    >
                        <Input addonBefore="+225" maxLength={10} placeholder="0700000000" />
                    </Form.Item>
                    <Form.Item name="adresse" label="Adresse">
                        <Input prefix={<EnvironmentOutlined />} placeholder="Adresse postale" />
                    </Form.Item>
                    <div className="agences-form-actions">
                        <Button onClick={() => setModalVisible(false)} className="agences-btn-cancel">
                            Annuler
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={modalLoading}
                            className="agences-btn-submit"
                        >
                            {editingItem ? 'Enregistrer' : "Créer l'agence"}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default Agences;
